import { PRODUCTS } from '../../data/products';
import { STORES } from '../../data/stores';
import type { IsoDate } from '../../domain/dates';
import { createCatalog, productOf } from '../../domain/price-book';
import type { Recipe } from '../../domain/recipe';
import type { Product, StoreId, Unit } from '../../domain/types';
import type { Language } from './messages';

const LOCALES: Readonly<Record<Language, string>> = { uk: 'uk-UA', de: 'de-DE' };

const UNITS: Readonly<Record<Language, Readonly<Record<Unit | 'kg' | 'l', string>>>> = {
  uk: { g: 'г', kg: 'кг', ml: 'мл', l: 'л', pcs: 'шт' },
  de: { g: 'g', kg: 'kg', ml: 'ml', l: 'l', pcs: 'Stk.' },
};

const DATE_STYLES = {
  'weekday-long': { weekday: 'long', day: { day: 'numeric', month: 'long' } },
  short: { weekday: 'short', day: { day: 'numeric', month: 'numeric' } },
} as const;

/**
 * DE: Pluralform nach den Regeln der Sprache (Ukrainisch kennt one, few, many, other).
 * EN: Plural form by the rules of the language (Ukrainian has one, few, many, other).
 */
export function plural(
  language: Language,
  count: number,
  forms: { one: string; few?: string; many?: string; other: string },
): string {
  const category = new Intl.PluralRules(LOCALES[language]).select(count);
  if (category === 'one') return forms.one;
  if (category === 'few') return forms.few ?? forms.other;
  if (category === 'many') return forms.many ?? forms.other;
  return forms.other;
}

/**
 * DE: Eurobetrag mit Komma und €-Zeichen, auch auf Ukrainisch.
 * EN: Euro amount with a comma and the € sign, in Ukrainian too.
 */
export function formatMoney(euros: number, language: Language): string {
  const options = { style: 'currency', currency: 'EUR', currencyDisplay: 'narrowSymbol' } as const;
  return new Intl.NumberFormat(LOCALES[language], options).format(euros);
}

/**
 * DE: Preis mit „≈“, wenn er auf geschätzten Katalogpreisen beruht.
 * EN: Price with "≈" when it rests on estimated catalogue prices.
 */
export function formatPrice(euros: number, language: Language, estimated: boolean): string {
  const money = formatMoney(euros, language);
  return estimated ? `≈ ${money}` : money;
}

/**
 * DE: Datum mit Wochentag; der erste Buchstabe wird großgeschrieben.
 * EN: Date with weekday; the first letter is capitalised.
 */
export function formatDate(
  date: IsoDate,
  language: Language,
  style: keyof typeof DATE_STYLES,
): string {
  const { weekday, day } = DATE_STYLES[style];
  const locale = LOCALES[language];
  const moment = new Date(`${date}T00:00:00Z`);
  // DE: Wochentag getrennt formatieren: Chrome setzt ihn sonst auf Ukrainisch in den Akkusativ („середу“).
  // EN: Format the weekday on its own: otherwise Chrome puts it in the Ukrainian accusative ("середу").
  const weekdayText = new Intl.DateTimeFormat(locale, { weekday, timeZone: 'UTC' }).format(moment);
  const dayText = new Intl.DateTimeFormat(locale, { ...day, timeZone: 'UTC' }).format(moment);
  return `${weekdayText.charAt(0).toLocaleUpperCase(locale)}${weekdayText.slice(1)}, ${dayText}`;
}

/**
 * DE: Menge für die Anzeige, z. B. „1,5 кг“ oder „3 Stk.“; ab 1000 g/ml in kg/l.
 * EN: Quantity for display, e.g. "1,5 кг" or "3 Stk."; from 1000 g/ml in kg/l.
 */
export function formatQuantity(quantity: number, unit: Unit, language: Language): string {
  const units = UNITS[language];
  const number = new Intl.NumberFormat(LOCALES[language], { maximumFractionDigits: 1 });
  if (unit === 'pcs') return `${number.format(quantity)} ${units.pcs}`;
  const isLarge = quantity >= 1000;
  const largeUnit = unit === 'g' ? units.kg : units.l;
  return isLarge
    ? `${number.format(quantity / 1000)} ${largeUnit}`
    : `${Math.round(quantity)} ${units[unit]}`;
}

/**
 * DE: Produktname: deutscher Regalname oder ukrainischer Name.
 * EN: Product name: German shelf name or Ukrainian name.
 */
export function productName(product: Product, language: Language): string {
  return language === 'de' ? product.nameDe : product.name;
}

/**
 * DE: Eingebaute Produkte als Aufzählung in der Sprache, z. B. „Ingwer, Garnelen TK“.
 * EN: Built-in products as a list in the language, e.g. "Ingwer, Garnelen TK".
 */
export function productList(productIds: readonly string[], language: Language): string {
  const catalog = createCatalog(PRODUCTS);
  return productIds.map((id) => productName(productOf(catalog, id), language)).join(', ');
}

/**
 * DE: Rezeptname in der Sprache; ohne Übersetzung so, wie er eingegeben wurde.
 * EN: Recipe name in the language; without a translation as it was typed.
 */
export function recipeName(recipe: Recipe, language: Language): string {
  return language === 'de' ? (recipe.nameDe ?? recipe.name) : recipe.name;
}

/**
 * DE: Arbeitsschritte in der Sprache; ohne Übersetzung so, wie sie eingegeben wurden.
 * EN: Steps in the language; without a translation as they were typed.
 */
export function recipeSteps(recipe: Recipe, language: Language): string[] {
  return language === 'de' ? (recipe.stepsDe ?? recipe.steps) : recipe.steps;
}

/**
 * DE: Name des Markts; er ist in beiden Sprachen gleich.
 * EN: Store name; it is the same in both languages.
 */
export function storeName(storeId: StoreId): string {
  return STORES.find((store) => store.id === storeId)?.name ?? storeId;
}

/**
 * DE: Texte nach den Regeln der Sprache vergleichen (z. B. „Ä“ wie „A“, „Ґ“ nach „Г“).
 * EN: Compare texts by the rules of the language (e.g. "Ä" like "A", "Ґ" after "Г").
 */
export function compareText(a: string, b: string, language: Language): number {
  return a.localeCompare(b, LOCALES[language]);
}
